// Shape of GET /api/bi/subscriber-cohorts/ (backend: fpf/crm/cohorts.py).
// A plan joins the cohort of the month of its first paid order and is
// followed month by month (M0, M1...) under two retention rules.

export type RetentionMode = 'orders' | 'coverage';
export const RETENTION_MODES: RetentionMode[] = ['orders', 'coverage'];

export type ByMode<T> = Record<RetentionMode, T>;

export interface CohortCell {
  offset: number;
  // The current month, still in progress: shown apart and left out of averages.
  partial: boolean;
  active: ByMode<number>;
  retention: ByMode<number | null>;
  orders: number;
  revenue: number;
  revenue_retention: number | null;
  cumulative_orders: number;
  cumulative_revenue: number;
  cumulative_orders_per_subscriber: number | null;
  // Cumulative revenue (excl. VAT) per subscriber since M0.
  ltv: number | null;
}

export type CohortState = 'active' | 'paused' | 'cancelled' | 'other';
export const COHORT_STATES: CohortState[] = [
  'active',
  'paused',
  'cancelled',
  'other',
];

export interface Cohort {
  month: string; // YYYY-MM-DD, first day of the month
  size: number;
  trial_size: number;
  states: Record<CohortState, number>;
  cells: CohortCell[];
}

// Weighted average per month offset over the cohorts that completed it.
export interface CohortAverage {
  offset: number;
  cohorts: number;
  subscribers: number;
  retention: ByMode<number | null>;
  revenue_retention: number | null;
  cumulative_orders_per_subscriber: number | null;
  ltv: number | null;
}

export type KpiRetentionOffset = 'm1' | 'm3' | 'm6' | 'm12';
export const KPI_RETENTION_OFFSETS: KpiRetentionOffset[] = [
  'm1',
  'm3',
  'm6',
  'm12',
];

export interface CohortKpis {
  subscribers: number;
  trial_share: number | null;
  active_now: number;
  active_now_share: number | null;
  retention: Record<KpiRetentionOffset, ByMode<number | null>>;
  ltv: {
    first_6_months: number | null;
    first_12_months: number | null;
  };
}

export interface ChurnReason {
  code: string; // '' = no reason recorded
  label: string;
  total: number;
  by_cohort: Record<string, number>;
}

export interface SubscriberCohortsReport {
  since: string;
  until: string;
  current_month: string;
  timezone: string;
  cohorts: Cohort[];
  average: CohortAverage[];
  kpis: CohortKpis;
  churn_reasons: ChurnReason[];
}

// Metrics the cohort table can show.
export type HeatmapMetric =
  | 'retention'
  | 'active'
  | 'revenue'
  | 'revenueRetention'
  | 'ltv'
  | 'ordersPerSubscriber';

export const HEATMAP_METRICS: HeatmapMetric[] = [
  'retention',
  'active',
  'revenue',
  'revenueRetention',
  'ltv',
  'ordersPerSubscriber',
];

export function cellValue(
  cell: CohortCell,
  metric: HeatmapMetric,
  mode: RetentionMode
): number | null {
  switch (metric) {
    case 'retention':
      return cell.retention[mode];
    case 'active':
      return cell.active[mode];
    case 'revenue':
      return cell.revenue;
    case 'revenueRetention':
      return cell.revenue_retention;
    case 'ltv':
      return cell.ltv;
    case 'ordersPerSubscriber':
      return cell.cumulative_orders_per_subscriber;
  }
}

// Counts and euros have no meaningful average across cohorts of different
// sizes, so their average row stays empty.
export function averageValue(
  average: CohortAverage,
  metric: HeatmapMetric,
  mode: RetentionMode
): number | null {
  switch (metric) {
    case 'retention':
      return average.retention[mode];
    case 'revenueRetention':
      return average.revenue_retention;
    case 'ltv':
      return average.ltv;
    case 'ordersPerSubscriber':
      return average.cumulative_orders_per_subscriber;
    default:
      return null;
  }
}

// Ratios shade on their own value; counts follow retention (a count is only
// "high" relative to its cohort) and the open-ended metrics are scaled
// against the largest value on screen.
export function heatIntensity(
  cell: CohortCell,
  metric: HeatmapMetric,
  mode: RetentionMode,
  maxValue: number
): number | null {
  let value: number | null;
  switch (metric) {
    case 'retention':
    case 'active':
      value = cell.retention[mode];
      break;
    case 'revenue':
    case 'revenueRetention':
      value = cell.revenue_retention;
      break;
    default: {
      const raw = cellValue(cell, metric, mode);
      value = raw === null || maxValue <= 0 ? null : raw / maxValue;
    }
  }
  if (value === null) return null;
  return Math.min(Math.max(value, 0), 1);
}

export function maxMetricValue(
  cohorts: Cohort[],
  metric: HeatmapMetric,
  mode: RetentionMode
): number {
  let max = 0;
  cohorts.forEach((cohort) =>
    cohort.cells.forEach((cell) => {
      if (cell.partial) return;
      const value = cellValue(cell, metric, mode);
      if (value !== null && value > max) max = value;
    })
  );
  return max;
}

export function maxOffset(cohorts: Cohort[]): number {
  return cohorts.reduce(
    (max, cohort) => Math.max(max, cohort.cells.length - 1),
    -1
  );
}

const MONTH_PATTERN = /^(\d{4})-(\d{2})/;

// '2025-10-01' -> '2025-10'
export function monthKey(month: string): string {
  const match = MONTH_PATTERN.exec(month);
  return match ? `${match[1]}-${match[2]}` : month;
}

// Months between two 'YYYY-MM' keys (end - start).
export function monthsBetween(start: string, end: string): number {
  const [startYear, startMonth] = start.split('-').map(Number);
  const [endYear, endMonth] = end.split('-').map(Number);
  return (endYear - startYear) * 12 + (endMonth - startMonth);
}

export function addMonths(month: string, count: number): string {
  const [year, monthNumber] = month.split('-').map(Number);
  const index = year * 12 + (monthNumber - 1) + count;
  const newYear = Math.floor(index / 12);
  const newMonth = (index % 12) + 1;
  return `${newYear}-${String(newMonth).padStart(2, '0')}`;
}

const percentFormat = new Intl.NumberFormat('es-ES', {
  style: 'percent',
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});
const euroFormat = new Intl.NumberFormat('es-ES', {
  style: 'currency',
  currency: 'EUR',
  maximumFractionDigits: 0,
});
const euroCentsFormat = new Intl.NumberFormat('es-ES', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const integerFormat = new Intl.NumberFormat('es-ES', {
  maximumFractionDigits: 0,
});
const decimalFormat = new Intl.NumberFormat('es-ES', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export const EMPTY_VALUE = '—';

export function formatPercent(value: number | null): string {
  return value === null ? EMPTY_VALUE : percentFormat.format(value);
}

export function formatEuro(value: number | null, cents = false): string {
  if (value === null) return EMPTY_VALUE;
  return (cents ? euroCentsFormat : euroFormat).format(value);
}

export function formatInteger(value: number | null): string {
  return value === null ? EMPTY_VALUE : integerFormat.format(value);
}

export function formatDecimal(value: number | null): string {
  return value === null ? EMPTY_VALUE : decimalFormat.format(value);
}

export function formatMetric(
  value: number | null,
  metric: HeatmapMetric
): string {
  switch (metric) {
    case 'retention':
    case 'revenueRetention':
      return formatPercent(value);
    case 'active':
      return formatInteger(value);
    case 'revenue':
      return formatEuro(value);
    case 'ltv':
      return formatEuro(value, true);
    case 'ordersPerSubscriber':
      return formatDecimal(value);
  }
}
